import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import bg2 from "../../../../images/bg2.png";

interface DropDownPopupProps {
  onSave: (dropItems: { response: string }[]) => void;
  savedItems: { response: string }[];
  onClose: () => void;
  inputType: string;
  rowEdit: { response: string }[];
}

const DropDownPopup: React.FC<DropDownPopupProps> = ({
  onSave,
  savedItems,
  onClose,
  inputType,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const [dropItems, setDropItems] = useState<{ response: string }[]>([]);
  const [itemScores, setItemScores] = useState<number[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [editingScore, setEditingScore] = useState<string>("");
  const [editingScoreError, setEditingScoreError] = useState<boolean>(false);
  const [boxItems, setBoxItems] = useState<{ response: string }[]>([]);
  const [newBoxValue, setNewBoxValue] = useState<string>("");
  const [radioItems, setRadioItems] = useState<{ response: string }[]>([]);
  const [newRadioValue, setNewRadioValue] = useState<string>("");
  const [errVar, setErrVar] = useState<boolean>(false);
  const [scoreError, setScoreError] = useState<boolean>(false);
  const [isAddingNewBox, setIsAddingNewBox] = useState<boolean>(false);
  const [isAddingNewRadio, setIsAddingNewRadio] = useState<boolean>(false);
  const [editingBoxIndex, setEditingBoxIndex] = useState<number | null>(null);
  const [editingBoxValue, setEditingBoxValue] = useState<string>("");
  const [editingRadioIndex, setEditingRadioIndex] = useState<number | null>(null);
  const [editingRadioValue, setEditingRadioValue] = useState<string>("");
  
  // State for viewing full text modal
  const [showFullTextModal, setShowFullTextModal] = useState<boolean>(false);
  const [fullTextContent, setFullTextContent] = useState<string>("");
  const [fullTextIndex, setFullTextIndex] = useState<number | null>(null);
  const [isEditingFullText, setIsEditingFullText] = useState<boolean>(false);
  const [editFullTextValue, setEditFullTextValue] = useState<string>("");
  
  // Refs to prevent double-saves when clicking save button after blur
  const saveButtonClickedRef = React.useRef<boolean>(false);

  // Auto-save current items then close the dialog
  const handleAutoSaveClose = () => {
    // If a real Save click already initiated, just close
    if (saveButtonClickedRef.current) {
      saveButtonClickedRef.current = false;
      onClose();
      return;
    }

    if (inputType === "dropdown") {
      // If currently adding new item with valid data, include it
      let finalItems = [...dropItems];
      let finalScores = [...itemScores];

      if (isAddingNew && newDropValue.trim() && newDropScore.trim() && !errVar && !scoreError) {
        finalItems = [...finalItems, { response: newDropValue.trim() }];
        finalScores = [...finalScores, Number(newDropScore) || 0];
      }

      if (editingIndex !== null && editingValue.trim() !== "") {
        const scoreValue = Number(editingScore);
        const isValidScore = !isNaN(scoreValue) && (scoreValue === 0 || scoreValue === 5 || scoreValue === 10);
        const isDuplicateEdit = finalItems.some(
          (item, i) => i !== editingIndex && item.response.trim().toLowerCase() === editingValue.trim().toLowerCase()
        );
        if (!isDuplicateEdit && isValidScore) {
          finalItems = finalItems.map((item, i) => (i === editingIndex ? { response: editingValue.trim() } : item));
          finalScores = finalScores.map((s, i) => (i === editingIndex ? scoreValue : s));
        }
      }

      onSave(finalItems.map((item, idx) => ({ response: item.response + "|" + (finalScores[idx] || 0) })));
    } else if (inputType === "checkbox") {
      let finalItems = [...boxItems];
      if (isAddingNewBox && newBoxValue.trim() && !errVar && !isDuplicate(boxItems, newBoxValue)) {
        finalItems = [...finalItems, { response: newBoxValue.trim() }];
      }
      if (editingBoxIndex !== null && editingBoxValue.trim() !== "") {
        const isDuplicateEdit = finalItems.some(
          (item, i) => i !== editingBoxIndex && item.response.trim().toLowerCase() === editingBoxValue.trim().toLowerCase()
        );
        if (!isDuplicateEdit) {
          finalItems = finalItems.map((item, i) => (i === editingBoxIndex ? { response: editingBoxValue.trim() } : item));
        }
      }
      onSave(finalItems);
    } else {
      // radio
      let finalItems = [...radioItems];
      if (isAddingNewRadio && newRadioValue.trim() && !errVar && !isDuplicate(radioItems, newRadioValue)) {
        finalItems = [...finalItems, { response: newRadioValue.trim() }];
      }
      if (editingRadioIndex !== null && editingRadioValue.trim() !== "") {
        const isDuplicateEdit = finalItems.some(
          (item, i) => i !== editingRadioIndex && item.response.trim().toLowerCase() === editingRadioValue.trim().toLowerCase()
        );
        if (!isDuplicateEdit) {
          finalItems = finalItems.map((item, i) => (i === editingRadioIndex ? { response: editingRadioValue.trim() } : item));
        }
      }
      onSave(finalItems);
    }

    // Provide user feedback
    // toast.success("Data saved automatically");
    onClose();
  };
  
  useEffect(() => {
    if (inputType === "dropdown") {
      // Parse saved items to extract response and score
      const parsedItems = savedItems.map(item => {
        const parts = item.response.split('|');
        return { response: parts[0]?.trim() || item.response };
      });
      const parsedScores = savedItems.map(item => {
        const parts = item.response.split('|');
        return parts.length > 1 && parts[1]?.trim() ? Number(parts[1].trim()) : 0;
      });
      setDropItems(parsedItems);
      setItemScores(parsedScores);
      setBoxItems([]);
      setRadioItems([]);
    } else if (inputType === "checkbox") {
      setDropItems([]);
      setBoxItems(savedItems);
      setRadioItems([]);
      setItemScores(savedItems.map(() => 0));
    } else if (inputType === "radio") {
      setDropItems([]);
      setBoxItems([]);
      setRadioItems(savedItems);
      setItemScores(savedItems.map(() => 0));
    }
    setErrVar(false);
    setScoreError(false);
    setEditingScoreError(false);
    setNewBoxValue("");
    setNewRadioValue("");
    setNewDropValue("");
    setNewDropScore("");
    setEditingIndex(null);
    setEditingValue("");
    setEditingScore("");
    setIsAddingNew(false);
    setIsAddingNewBox(false);
    setIsAddingNewRadio(false);
    setEditingBoxIndex(null);
    setEditingBoxValue("");
    setEditingRadioIndex(null);
    setEditingRadioValue("");
  }, [inputType, savedItems]);

  // Helper for duplicate check
  const isDuplicate = (arr: { response: string }[], val: string) =>
    arr.some(
      (item) => item.response.trim().toLowerCase() === val.trim().toLowerCase()
    );

  // Drop Down (input box for option, user sets score)
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [newDropValue, setNewDropValue] = useState<string>("");
  const [newDropScore, setNewDropScore] = useState<string>("");
  const questionInputRef = React.useRef<HTMLInputElement>(null);
  
  const dropAddItem = () => {
    const scoreValue = Number(newDropScore);
    const isValidScore = !isNaN(scoreValue) && 
                        (scoreValue === 0 || scoreValue === 5 || scoreValue === 10);
    
    if (
      newDropValue.trim() !== "" &&
      !isDuplicate(dropItems, newDropValue) &&
      newDropScore.trim() !== "" &&
      isValidScore
    ) {
      setDropItems([...dropItems, { response: newDropValue }]);
      setItemScores([...itemScores, scoreValue]);
      setNewDropValue("");
      setNewDropScore("");
      setErrVar(false);
      setScoreError(false);
      setIsAddingNew(false);
    }
  };

  const startAddingNew = () => {
    setIsAddingNew(true);
    setNewDropValue("");
    setNewDropScore("");
    setErrVar(false);
    setScoreError(false);
    setTimeout(() => questionInputRef.current?.focus(), 0);
  };

  const cancelAddingNew = () => {
    setIsAddingNew(false);
    setNewDropValue("");
    setNewDropScore("");
    setErrVar(false);
    setScoreError(false);
  };

  const dropSaveAndClose = () => {
    // If currently adding new item with valid data, save it first
    if (isAddingNew && newDropValue.trim() && newDropScore.trim() && !errVar && !scoreError) {
      dropAddItem();
    }

    // Prepare arrays for saving
    let finalItems = [...dropItems];
    let finalScores = [...itemScores];

    // If there's an item being edited, save it to the list first
    if (editingIndex !== null && editingValue.trim() !== "") {
      const scoreValue = Number(editingScore);
      const isValidScore = !isNaN(scoreValue) && 
                          (scoreValue === 0 || scoreValue === 5 || scoreValue === 10);
      
      const isDuplicateEdit = dropItems.some(
        (item, i) => i !== editingIndex && item.response.trim().toLowerCase() === editingValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit && isValidScore) {
        finalItems = finalItems.map((item, i) => 
          i === editingIndex ? { response: editingValue } : item
        );
        finalScores = finalScores.map((score, i) => 
          i === editingIndex ? scoreValue : score
        );
      }
    }

    // Pass data to parent component (not saving to database yet)
    // Data will be saved to database when "Add Question +" or "Save Question" button is clicked
    onSave(
      finalItems.map((item, idx) => ({
        response: item.response + "|" + (finalScores[idx] || 0),
      }))
    );

    setNewDropValue("");
    setNewDropScore("");
    setEditingIndex(null);
    setEditingValue("");
    setEditingScore("");
    setEditingScoreError(false);
    setErrVar(false);
    setIsAddingNew(false);
    
    // Show success message
    toast.success("Data added successfully");
    
    // Close the modal
    onClose();
  };
  
  const dropRemoveItem = (index: number) => {
    const newDropItems = dropItems.filter((_, i) => i !== index);
    const newItemScores = itemScores.filter((_, i) => i !== index);
    setDropItems(newDropItems);
    setItemScores(newItemScores);
    // Immediately notify parent so select view updates
    onSave(
      newDropItems.map((item, idx) => ({ response: item.response + "|" + (newItemScores[idx] || 0) }))
    );
  };

  const dropEditItem = (index: number) => {
    setEditingIndex(index);
    setEditingValue(dropItems[index].response);
    setEditingScore(String(itemScores[index]));
  };

  const dropSaveEdit = (index: number) => {
    const scoreValue = Number(editingScore);
    const isValidScore = editingScore.trim() !== "" && !isNaN(scoreValue) && 
                        (scoreValue === 0 || scoreValue === 5 || scoreValue === 10);
    
    if (editingValue.trim() !== "" && isValidScore) {
      // Check if there's an actual change
      const hasChange = dropItems[index].response !== editingValue.trim() || 
                        itemScores[index] !== scoreValue;
      
      // Check for duplicates (excluding current item)
      const isDuplicateEdit = dropItems.some(
        (item, i) => i !== index && item.response.trim().toLowerCase() === editingValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit) {
        const updatedItems = dropItems.map((item, i) => 
          i === index ? { response: editingValue.trim() } : item
        );
        const updatedScores = itemScores.map((score, i) => 
          i === index ? scoreValue : score
        );
        
        setDropItems(updatedItems);
        setItemScores(updatedScores);
        setEditingIndex(null);
        setEditingValue("");
        setEditingScore("");
        setEditingScoreError(false);
        setErrVar(false);
        
        // Show appropriate message based on whether changes were made
        if (hasChange) {
          toast.success("Your changes have been saved successfully!");
        } else {
          toast.info("No changes detected. Data remains unchanged.");
        }
      }
    }
  };

  const dropCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
    setEditingScore("");
    setEditingScoreError(false);
  };

  const onChangeDropval = (val: string) => {
    setNewDropValue(val);
    setErrVar(isDuplicate(dropItems, val));
  };
  const onChangeDropScore = (val: string) => {
    setNewDropScore(val);
    // Check if the value contains any non-numeric characters
    if (val && !/^\d*$/.test(val)) {
      setScoreError(true);
    } else if (val && val.trim() !== "" && val !== "0" && val !== "5" && val !== "10") {
      // Only allow 0, 5, or 10
      setScoreError(true);
    } else {
      setScoreError(false);
    }
  };

  // Handle Enter key press for adding items
  const handleKeyPress = (e: React.KeyboardEvent, field: 'question' | 'score') => {
    if (e.key === "Enter" && !errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) {
      e.preventDefault();
      dropAddItem();
    } else if (e.key === "Tab" && field === 'question' && newDropValue.trim()) {
      // Allow natural tab to score field
      setErrVar(false);
    }
  };

  // Check Box
  const boxAddItem = () => {
    if (newBoxValue.trim() !== "" && !isDuplicate(boxItems, newBoxValue)) {
      setBoxItems([...boxItems, { response: newBoxValue }]);
      setNewBoxValue("");
      setErrVar(false);
      setIsAddingNewBox(false);
    }
  };
  
  const startAddingNewBox = () => {
    setIsAddingNewBox(true);
    setNewBoxValue("");
    setErrVar(false);
    setTimeout(() => questionInputRef.current?.focus(), 0);
  };

  const cancelAddingNewBox = () => {
    setIsAddingNewBox(false);
    setNewBoxValue("");
    setErrVar(false);
  };

  const boxSaveAndClose = () => {
    // If there's a pending item with valid data, add it first
    if (isAddingNewBox && newBoxValue.trim() && !errVar && !isDuplicate(boxItems, newBoxValue)) {
      boxAddItem();
    }

    // Prepare final items
    let finalItems = [...boxItems];
    
    // If we just added an item above, include it
    if (isAddingNewBox && newBoxValue.trim() && !errVar && !isDuplicate(boxItems, newBoxValue)) {
      finalItems = [...boxItems, { response: newBoxValue }];
    }

    // If there's an item being edited, save it to the list first
    if (editingBoxIndex !== null && editingBoxValue.trim() !== "") {
      const isDuplicateEdit = boxItems.some(
        (item, i) => i !== editingBoxIndex && item.response.trim().toLowerCase() === editingBoxValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit) {
        finalItems = finalItems.map((item, i) => 
          i === editingBoxIndex ? { response: editingBoxValue } : item
        );
      }
    }

    // Pass data to parent component (not saving to database yet)
    // Data will be saved to database when "Add Question +" or "Save Question" button is clicked
    onSave(finalItems);
    
    setNewBoxValue("");
    setErrVar(false);
    setIsAddingNewBox(false);
    setEditingBoxIndex(null);
    setEditingBoxValue("");
    
    // Show success message
    toast.success("Data added successfully");
    
    onClose();
  };
  
  const boxEditItem = (index: number) => {
    setEditingBoxIndex(index);
    setEditingBoxValue(boxItems[index].response);
  };

  const boxSaveEdit = (index: number) => {
    if (editingBoxValue.trim() !== "") {
      // Check if there's an actual change
      const hasChange = boxItems[index].response !== editingBoxValue.trim();
      
      // Check for duplicates (excluding current item)
      const isDuplicateEdit = boxItems.some(
        (item, i) => i !== index && item.response.trim().toLowerCase() === editingBoxValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit) {
        const updatedItems = boxItems.map((item, i) => 
          i === index ? { response: editingBoxValue.trim() } : item
        );
        
        setBoxItems(updatedItems);
        setEditingBoxIndex(null);
        setEditingBoxValue("");
        setErrVar(false);
        
        // Show appropriate message based on whether changes were made
        if (hasChange) {
          toast.success("Your changes have been saved successfully!");
        } else {
          toast.info("No changes detected. Data remains unchanged.");
        }
      } else {
        setErrVar(true);
      }
    }
  };

  const boxCancelEdit = () => {
    setEditingBoxIndex(null);
    setEditingBoxValue("");
    setErrVar(false);
  };

  const boxRemoveItem = (index: number) => {
    const newBoxItems = boxItems.filter((_, i) => i !== index);
    setBoxItems(newBoxItems);
    // Immediately notify parent so select view updates
    onSave(newBoxItems);
  };
  const onChangeBoxval = (val: string) => {
    setNewBoxValue(val);
    setErrVar(isDuplicate(boxItems, val));
  };

  // Radio
  const radioAddItem = () => {
    if (newRadioValue.trim() !== "" && !isDuplicate(radioItems, newRadioValue)) {
      setRadioItems([...radioItems, { response: newRadioValue }]);
      setNewRadioValue("");
      setErrVar(false);
      setIsAddingNewRadio(false);
    }
  };
  
  const startAddingNewRadio = () => {
    setIsAddingNewRadio(true);
    setNewRadioValue("");
    setErrVar(false);
    setTimeout(() => questionInputRef.current?.focus(), 0);
  };

  const cancelAddingNewRadio = () => {
    setIsAddingNewRadio(false);
    setNewRadioValue("");
    setErrVar(false);
  };

  const radioSaveAndClose = () => {
    // If there's a pending item with valid data, add it first
    if (isAddingNewRadio && newRadioValue.trim() && !errVar && !isDuplicate(radioItems, newRadioValue)) {
      radioAddItem();
    }

    // Prepare final items
    let finalItems = [...radioItems];
    
    // If we just added an item above, include it
    if (isAddingNewRadio && newRadioValue.trim() && !errVar && !isDuplicate(radioItems, newRadioValue)) {
      finalItems = [...radioItems, { response: newRadioValue }];
    }

    // If there's an item being edited, save it to the list first
    if (editingRadioIndex !== null && editingRadioValue.trim() !== "") {
      const isDuplicateEdit = radioItems.some(
        (item, i) => i !== editingRadioIndex && item.response.trim().toLowerCase() === editingRadioValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit) {
        finalItems = finalItems.map((item, i) => 
          i === editingRadioIndex ? { response: editingRadioValue } : item
        );
      }
    }

    // Pass data to parent component (not saving to database yet)
    // Data will be saved to database when "Add Question +" or "Save Question" button is clicked
    onSave(finalItems);
    
    setNewRadioValue("");
    setErrVar(false);
    setIsAddingNewRadio(false);
    setEditingRadioIndex(null);
    setEditingRadioValue("");
    
    // Show success message
    toast.success("Data added successfully");
    
    onClose();
  };
  
  const radioEditItem = (index: number) => {
    setEditingRadioIndex(index);
    setEditingRadioValue(radioItems[index].response);
  };

  const radioSaveEdit = (index: number) => {
    if (editingRadioValue.trim() !== "") {
      // Check if there's an actual change
      const hasChange = radioItems[index].response !== editingRadioValue.trim();
      
      // Check for duplicates (excluding current item)
      const isDuplicateEdit = radioItems.some(
        (item, i) => i !== index && item.response.trim().toLowerCase() === editingRadioValue.trim().toLowerCase()
      );
      
      if (!isDuplicateEdit) {
        const updatedItems = radioItems.map((item, i) => 
          i === index ? { response: editingRadioValue.trim() } : item
        );
        
        setRadioItems(updatedItems);
        setEditingRadioIndex(null);
        setEditingRadioValue("");
        setErrVar(false);
        
        // Show appropriate message based on whether changes were made
        if (hasChange) {
          toast.success("Your changes have been saved successfully!");
        } else {
          toast.info("No changes detected. Data remains unchanged.");
        }
      } else {
        setErrVar(true);
      }
    }
  };

  const radioCancelEdit = () => {
    setEditingRadioIndex(null);
    setEditingRadioValue("");
    setErrVar(false);
  };

  const radioRemoveItem = (index: number) => {
    const newRadioItems = radioItems.filter((_, i) => i !== index);
    setRadioItems(newRadioItems);
    // Immediately notify parent so select view updates
    onSave(newRadioItems);
  };
  const onChangeRadioval = (val: string) => {
    setNewRadioValue(val);
    setErrVar(isDuplicate(radioItems, val));
  };

  // Dynamic content
  let title = "";
  let items: { response: string }[] = [];
  let newValue = "";
  let onChange: (val: string) => void = () => {};
  let onAdd: () => void = () => {};
  let onRemove: (index: number) => void = () => {};
  let onSaveAndClose: () => void = () => {};
  let placeholder = "";

  if (inputType === "dropdown") {
    title = "Create Drop Down Options";
    items = dropItems;
    newValue = newDropValue;
    onChange = onChangeDropval;
    onAdd = dropAddItem;
    onRemove = dropRemoveItem;
    onSaveAndClose = dropSaveAndClose;
    placeholder = "Enter your drop down option here.";
  } else if (inputType === "checkbox") {
    title = "Create Check Box Options";
    items = boxItems;
    newValue = newBoxValue;
    onChange = onChangeBoxval;
    onAdd = boxAddItem;
    onRemove = boxRemoveItem;
    onSaveAndClose = boxSaveAndClose;
    placeholder = "Enter your check box option here.";
  } else {
    title = "Create Radio Button Options";
    items = radioItems;
    newValue = newRadioValue;
    onChange = onChangeRadioval;
    onAdd = radioAddItem;
    onRemove = radioRemoveItem;
    onSaveAndClose = radioSaveAndClose;
    placeholder = "Enter your radio button option here.";
  }

  const isDropdownSaveEnabled = () => {
    if (inputType !== "dropdown") return true;
    // Enabled if there are existing dropdown items
    if (dropItems.length > 0) return true;
    // Or if user is currently adding a valid new dropdown item
    if (isAddingNew && newDropValue.trim() && newDropScore.trim() && !errVar && !scoreError) return true;
    // Or if user is editing an item with valid values
    if (
      editingIndex !== null &&
      editingValue.trim() &&
      editingScore.trim() &&
      !editingScoreError &&
      (editingScore === "0" || editingScore === "5" || editingScore === "10")
    ) return true;
    return false;
  };

  const isSaveEnabled = () => {
    if (inputType === "dropdown") return isDropdownSaveEnabled();
    if (inputType === "checkbox") {
      if (boxItems.length > 0) return true;
      if (isAddingNewBox && newBoxValue.trim() && !errVar) return true;
      if (editingBoxIndex !== null && editingBoxValue.trim()) return true;
      return false;
    }
    // radio
    if (inputType === "radio") {
      if (radioItems.length > 0) return true;
      if (isAddingNewRadio && newRadioValue.trim() && !errVar) return true;
      if (editingRadioIndex !== null && editingRadioValue.trim()) return true;
      return false;
    }
    return false;
  };
  return (
    <Dialog
      open
      onClose={() => handleAutoSaveClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: 0,
          width: isPhone ? "90vw" : 400,
          maxWidth: isPhone ? 320 : 400,
          minWidth: isPhone ? "unset" : 360,
          minHeight: isPhone ? "60vh" : "unset",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          mx: isPhone ? 7 : "unset",
          my: isPhone ? "auto" : "auto",
          translate:isPhone?'25px':'0px',
          overflowX:isPhone?'auto':'hidden',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", // Center the content horizontally
          pr: 1,
          // background: `url(${bg2}) center/cover no-repeat`,
          color: '#fff',
          width: '100%',
          height: 48,
          minHeight: 0,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          py: 0,
          mb: 1.5,
          '& .MuiTypography-root': { lineHeight: 1.2 },
          background:'#d5d5d6'
        }}
      >
        <Typography component="div" sx={{ fontWeight: "bold", color: 'black', fontSize: '1.05rem', lineHeight: 1.2, flex: 1, textAlign: 'center' }}>
          {title}
        </Typography>
        <IconButton onClick={() => handleAutoSaveClose()} size="small" sx={{ color: '#000', p: 0.5, position: 'absolute', right: 8, top: 8,bgcolor:'#FF5E4F' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ width: "100%", mt: 1, px: 3, pb: 2 }}>
        <Stack spacing={1}>
          {inputType === "dropdown" && dropItems.map((item, index) => (
            <Stack
              key={index}
              direction="column"
              spacing={0}
              sx={{ 
                width: "100%",
                mb: editingIndex === index && editingScoreError ? 2 : 0
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0}
                sx={{ 
                  width: "100%", 
                  minHeight: 48,
                  bgcolor: 'transparent',
                  borderRadius: 1,
                  transition: 'all 0.2s'
                }}
              >
              {/* Option input (click to edit) */}
              <Box
                onClick={() => editingIndex !== index && dropEditItem(index)}
                sx={{ 
                  display: 'inline-block', 
                  width: 260, 
                  mr: 1,
                  ml: 2,
                  cursor: editingIndex !== index ? 'pointer' : 'default'
                }}
              >
              <TextField
                value={editingIndex === index ? editingValue : item.response}
                disabled={editingIndex !== index}
                onChange={e => setEditingValue(e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                sx={{ 
                  pointerEvents: editingIndex !== index ? 'none' : 'auto',
                  '& .MuiInputBase-input': {
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'auto'
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#666',
                    bgcolor: '#f5f5f5'
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    bgcolor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    }
                  },
                  '& .MuiOutlinedInput-root:hover': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" && editingIndex === index) {
                    dropSaveEdit(index);
                  } else if (e.key === "Tab" && editingIndex === index) {
                    e.preventDefault();
                    dropSaveEdit(index);
                  } else if (e.key === "Escape") {
                    dropCancelEdit();
                  }
                }}
                onBlur={() => {
                  if (editingIndex === index && !saveButtonClickedRef.current) {
                    setTimeout(() => {
                      if (!saveButtonClickedRef.current) {
                        dropSaveEdit(index);
                      }
                      saveButtonClickedRef.current = false;
                    }, 150);
                  }
                }}
              />
              </Box>
              {/* Score input (editable inline) */}
              <Box
                onClick={() => editingIndex !== index && dropEditItem(index)}
                sx={{ 
                  display: 'inline-block', 
                  width: 80, 
                  mr: 1,
                  cursor: editingIndex !== index ? 'pointer' : 'default'
                }}
              >
              <TextField
                value={editingIndex === index ? editingScore : itemScores[index]}
                disabled={editingIndex !== index}
                onChange={e => {
                  if (editingIndex === index) {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setEditingScore(val);
                    // Validate score: only allow 0, 5, or 10
                    if (val && val.trim() !== "" && val !== "0" && val !== "5" && val !== "10") {
                      setEditingScoreError(true);
                    } else {
                      setEditingScoreError(false);
                    }
                  }
                }}
                error={editingIndex === index && editingScoreError}
                size="small"
                variant="outlined"
                fullWidth
                sx={{ 
                  pointerEvents: editingIndex !== index ? 'none' : 'auto',
                  '& .MuiInputBase-input': { textAlign: 'center' },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#666',
                    bgcolor: '#f5f5f5'
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    bgcolor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    }
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  }
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                onKeyDown={e => {
                  if (e.key === "Enter" && editingIndex === index) {
                    dropSaveEdit(index);
                  } else if (e.key === "Tab" && editingIndex === index) {
                    e.preventDefault();
                    dropSaveEdit(index);
                  } else if (e.key === "Escape") {
                    dropCancelEdit();
                  }
                }}
                onBlur={() => {
                  if (editingIndex === index && !saveButtonClickedRef.current) {
                    setTimeout(() => {
                      if (!saveButtonClickedRef.current) {
                        dropSaveEdit(index);
                      }
                      saveButtonClickedRef.current = false;
                    }, 150);
                  }
                }}
              />
              </Box>
              {editingIndex === index ? (
                <Tooltip title="Save" arrow>
                  <span>
                    <IconButton
                      onMouseDown={() => { saveButtonClickedRef.current = true; }}
                      onClick={() => dropSaveEdit(index)}
                      color="primary"
                      size="small"
                      aria-label="Save"
                      disabled={!editingValue.trim() || !editingScore.trim() || editingScoreError || !(editingScore === "0" || editingScore === "5" || editingScore === "10")}
                      sx={{ 
                        mr: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: 'rgba(1, 45, 138, 0.08)'
                        }
                      }}
                    >
                      <SaveIcon style={{color: (!editingValue.trim() || !editingScore.trim() || editingScoreError || !(editingScore === "0" || editingScore === "5" || editingScore === "10")) ? '#ccc' : '#012d8a'}} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={() => dropEditItem(index)}
                    color="primary"
                    size="small"
                    aria-label="Edit"
                    sx={{ 
                      mr: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: 'rgba(1, 45, 138, 0.08)'
                      }
                    }}
                  >
                    <EditIcon style={{color:'#012d8a'}} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete" arrow>
                <IconButton
                  onClick={() => dropRemoveItem(index)}
                  color="error"
                  size="small"
                  aria-label="Remove"
                  sx={{ 
                    mr: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                >
                  <DeleteIcon style={{color:'#d32f2f'}} />
                </IconButton>
              </Tooltip>
              </Stack>
              {/* Error message for editing score */}
              {editingIndex === index && editingScoreError && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Score must be 0, 5, or 10 only
                </Typography>
              )}
            </Stack>
          ))}
          {inputType === "dropdown" && isAddingNew && (
            <Stack
              direction="column"
              spacing={0}
              sx={{ width: '100%', position: 'relative', mb: scoreError || errVar ? 2 : 0 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                // spacing={-0.01}
                sx={{ 
                  width: '100%', 
                  minHeight: 48,
                  bgcolor: 'transparent',
                  borderRadius: 1,
                  py: 0.5,
                }}
              >
                <TextField
                  inputRef={questionInputRef}
                  value={newDropValue}
                  onChange={e => onChangeDropval(e.target.value)}
                  placeholder={placeholder}
                  size="small"
                  variant="outlined"
                  autoFocus
                  error={errVar}
                  multiline
                  minRows={2}
                  maxRows={6}
                  style={{translate:'18px'}}
                  sx={{ 
                    width: 260, 
                    mr: 1,
                    ml: 2,
                    '& .MuiFormHelperText-root': {
                      display: 'none'
                    },
                    '& .MuiInputBase-input': {
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflow: 'auto'
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                      '& fieldset': {
                        borderColor: '#FF5E4F'
                      }
                    }
                  }}
                  onKeyDown={e => handleKeyPress(e, 'question')}
                  onBlur={() => {
                    // Auto-save on blur if both fields have valid data
                    if (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) {
                      setTimeout(() => {
                        if (!saveButtonClickedRef.current) {
                          dropAddItem();
                        }
                        saveButtonClickedRef.current = false;
                      }, 150);
                    }
                  }}
                />
                <TextField
                  value={newDropScore}
                  onChange={e => onChangeDropScore(e.target.value)}
                  placeholder="Score"
                  size="small"
                  variant="outlined"
                  error={scoreError}
                  style={{translate:'18px'}}
                  sx={{ 
                    width: 80, 
                    mr: 1, 
                    '& .MuiInputBase-input': { textAlign: 'center' },
                    '& .MuiFormHelperText-root': {
                      display: 'none'
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                      '& fieldset': {
                        borderColor: '#FF5E4F'
                      }
                    }
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  onKeyDown={e => handleKeyPress(e, 'score')}
                  onBlur={() => {
                    // Auto-save on blur if both fields have valid data
                    if (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) {
                      setTimeout(() => {
                        if (!saveButtonClickedRef.current) {
                          dropAddItem();
                        }
                        saveButtonClickedRef.current = false;
                      }, 150);
                    }
                  }}
                />
                <IconButton
                  onClick={() => dropEditItem(0)}
                  color="primary"
                  size="small"
                  aria-label="Edit"
                  sx={{ 
                    mr: 1, 
                    visibility: 'hidden',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}
                  disabled
                >
                  <EditIcon style={{color:'#012d8a'}} />
                </IconButton>
                <IconButton
                  onMouseDown={() => { saveButtonClickedRef.current = true; }}
                  onClick={dropAddItem}
                  color="primary"
                  disabled={errVar || !newDropValue.trim() || !newDropScore.trim() || scoreError}
                  size="small"
                  aria-label="Add"
                  style={{translate:'-20px'}}
                  sx={{ 
                    mr: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) ? 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)' : 'transparent',
                    color: (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) ? '#fff' : 'inherit',
                    '&:hover': {
                      background: (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) ? 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)' : 'transparent'
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <AddIcon style={{color: (!errVar && newDropValue.trim() && newDropScore.trim() && !scoreError) ? '#fff' : '#FF5E4F'}} />
                </IconButton>
                <IconButton
                  onClick={cancelAddingNew}
                  size="small"
                  aria-label="Cancel"
                   style={{translate:'-20px'}}
                  sx={{ 
                    mr: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#d32f2f',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)',
                      color: '#d32f2f'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" style={{color:'#d32f2f'}} />
                </IconButton>
              </Stack>
              {/* Error messages displayed below without distorting layout */}
              {(errVar || scoreError) && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  {errVar && "Please enter unique options. "}
                  {scoreError && "Score must be 0, 5, or 10 only"}
                </Typography>
              )}
              {/* Helper text for better UX */}
              {!errVar && !scoreError && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.70rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%',
                    color: '#666',
                    fontStyle: 'italic'
                  }}
                >
                  Score: 0, 5, or 10 • Press Enter to add • ESC to cancel
                </Typography>
              )}
            </Stack>
          )}
          {/* Add New Option Button */}
          {inputType === "dropdown" && !isAddingNew && (
            <Button
              onClick={startAddingNew}
              variant="contained"
              // color="primary"
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                border: 'none',
                py: 1,
                width: '50%',
                alignSelf: 'center',
                background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)'
                }
              }}
            >
              Add New Option
            </Button>
          )}
          {/* Display existing items for checkbox and radio */}
          {inputType === "checkbox" && boxItems.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ 
                width: "100%", 
                minHeight: 48,
                bgcolor: 'transparent',
                borderRadius: 1,
                transition: 'all 0.2s'
              }}
            >
              <Box
                onClick={() => editingBoxIndex !== index && boxEditItem(index)}
                sx={{ 
                  flex: 1, 
                  maxWidth: 320,
                  ml: 3,
                  translate:'10px',
                  cursor: editingBoxIndex !== index ? 'pointer' : 'default'
                }}
              >
              <TextField
                value={editingBoxIndex === index ? editingBoxValue : item.response}
                disabled={editingBoxIndex !== index}
                onChange={e => setEditingBoxValue(e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                sx={{ 
                  pointerEvents: editingBoxIndex !== index ? 'none' : 'auto',
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    color: '#000',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'auto'
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#666',
                    bgcolor: '#f5f5f5',
                     
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    bgcolor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    }
                  },
                  '& .MuiOutlinedInput-root:hover': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  },
                 
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" && editingBoxIndex === index) {
                    boxSaveEdit(index);
                  } else if (e.key === "Tab" && editingBoxIndex === index) {
                    e.preventDefault();
                    boxSaveEdit(index);
                  } else if (e.key === "Escape") {
                    boxCancelEdit();
                  }
                }}
                onBlur={() => {
                  if (editingBoxIndex === index && !saveButtonClickedRef.current) {
                    setTimeout(() => {
                      if (!saveButtonClickedRef.current) {
                        boxSaveEdit(index);
                      }
                      saveButtonClickedRef.current = false;
                    }, 150);
                  }
                }}
              />
              </Box>
              {editingBoxIndex === index ? (
                <Tooltip title="Save" arrow>
                  <span>
                    <IconButton
                      onMouseDown={() => { saveButtonClickedRef.current = true; }}
                      onClick={() => boxSaveEdit(index)}
                      // color="primary"
                      size="small"
                      aria-label="Save"
                      disabled={!editingBoxValue.trim()}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: 'rgba(1, 45, 138, 0.08)'
                        },
                        color:'#FF5E4F'
                      }}
                    >
                      <SaveIcon style={{color: !editingBoxValue.trim() ? '#ccc' : '#FF5E4F'}} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={() => boxEditItem(index)}
                    // color="primary"
                    size="small"
                    aria-label="Edit"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: 'rgba(1, 45, 138, 0.08)'
                      },
                      color:'#FF5E4F'
                    }}
                  >
                    <EditIcon style={{color:'##FF5E4F'}} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete" arrow>
                <IconButton
                  onClick={() => boxRemoveItem(index)}
                  color="error"
                  size="small"
                  aria-label="Remove"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                >
                  <DeleteIcon style={{color:'#d32f2f'}} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          {inputType === "radio" && radioItems.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ 
                width: "100%", 
                minHeight: 48,
                bgcolor: 'transparent',
                borderRadius: 1,
                transition: 'all 0.2s'
              }}
            >
              <Box
                onClick={() => editingRadioIndex !== index && radioEditItem(index)}
                sx={{ 
                  flex: 1, 
                  maxWidth: 320,
                  ml:3,
                  cursor: editingRadioIndex !== index ? 'pointer' : 'default',
                  translate:'10px'
                }}
              >
              <TextField
                value={editingRadioIndex === index ? editingRadioValue : item.response}
                disabled={editingRadioIndex !== index}
                onChange={e => setEditingRadioValue(e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                sx={{ 
                  pointerEvents: editingRadioIndex !== index ? 'none' : 'auto',
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    color: '#000',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'auto'
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: '#666',
                    bgcolor: '#f5f5f5'
                  },
                  '& .MuiOutlinedInput-root.Mui-disabled': {
                    bgcolor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    }
                  },
                  '& .MuiOutlinedInput-root:hover': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': {
                    '& fieldset': {
                      borderColor: '#FF5E4F'
                    }
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" && editingRadioIndex === index) {
                    radioSaveEdit(index);
                  } else if (e.key === "Tab" && editingRadioIndex === index) {
                    e.preventDefault();
                    radioSaveEdit(index);
                  } else if (e.key === "Escape") {
                    radioCancelEdit();
                  }
                }}
                onBlur={() => {
                  if (editingRadioIndex === index && !saveButtonClickedRef.current) {
                    setTimeout(() => {
                      if (!saveButtonClickedRef.current) {
                        radioSaveEdit(index);
                      }
                      saveButtonClickedRef.current = false;
                    }, 150);
                  }
                }}
              />
              </Box>
              {editingRadioIndex === index ? (
                <Tooltip title="Save" arrow>
                  <span>
                    <IconButton
                      onMouseDown={() => { saveButtonClickedRef.current = true; }}
                      onClick={() => radioSaveEdit(index)}
                      // color="primary"
                      size="small"
                      aria-label="Save"
                      disabled={!editingRadioValue.trim()}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: 'rgba(1, 45, 138, 0.08)'
                        },
                        translate:'4px',
                        color:'#FF5E4F'
                      }}
                    >
                      <SaveIcon style={{color: !editingRadioValue.trim() ? '#ccc' : '#FF5E4F'}} />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Edit" arrow>
                  <IconButton
                    onClick={() => radioEditItem(index)}
                    // color="primary"
                    size="small"
                    aria-label="Edit"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      '&:hover': {
                        bgcolor: 'rgba(1, 45, 138, 0.08)'
                      },
                      color:'#FF5E4F'
                    }}
                  >
                    <EditIcon style={{color:'##FF5E4F'}} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete" arrow>
                <IconButton
                  onClick={() => radioRemoveItem(index)}
                  color="error"
                  size="small"
                  aria-label="Remove"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                >
                  <DeleteIcon style={{color:'#d32f2f'}} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          {/* Add new item input for checkbox and radio */}
          {inputType === "checkbox" && isAddingNewBox && (
            <Stack 
              direction="column"
              spacing={0}
              sx={{ width: '100%', position: 'relative', mb: errVar ? 2 : 0 }}
            >
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="center" 
                spacing={0}
                sx={{ 
                  width: "100%", 
                  minHeight: 48
                }}
              >
                <TextField
                  inputRef={questionInputRef}
                  value={newBoxValue}
                  onChange={(e) => onChangeBoxval(e.target.value)}
                  placeholder={placeholder}
                  size="small"
                  variant="outlined"
                  autoFocus
                  error={errVar}
                  multiline
                  minRows={2}
                  maxRows={6}
                  sx={{ 
                    flex: 1,
                    maxWidth: 320,
                    mr: 1,
                    ml: 3,
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      color: '#000',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflow: 'auto'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#999',
                      opacity: 1
                    },
                    '& .MuiFormHelperText-root': {
                      display: 'none'
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                      '& fieldset': {
                        borderColor: '#FF5E4F'
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !errVar && newBoxValue.trim()) {
                      boxAddItem();
                    } else if (e.key === "Escape") {
                      cancelAddingNewBox();
                    }
                  }}
                  onBlur={() => {
                    // Auto-save on blur if field has valid data
                    if (!errVar && newBoxValue.trim()) {
                      setTimeout(() => {
                        if (!saveButtonClickedRef.current) {
                          boxAddItem();
                        }
                        saveButtonClickedRef.current = false;
                      }, 150);
                    }
                  }}
                />
                <IconButton
                  onMouseDown={() => { saveButtonClickedRef.current = true; }}
                  onClick={boxAddItem}
                  color="primary"
                  disabled={errVar || !newBoxValue.trim()}
                  size="small"
                  aria-label="Add"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: (!errVar && newBoxValue.trim()) ? 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)' : 'transparent',
                    '&:hover': {
                      background: (!errVar && newBoxValue.trim()) ? 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)' : 'transparent'
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <AddIcon style={{color: (!errVar && newBoxValue.trim()) ? '#fff' : '#FF5E4F'}} />
                </IconButton>
                <IconButton
                  onClick={cancelAddingNewBox}
                  size="small"
                  aria-label="Cancel"
                  sx={{ 
                    mr: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#d32f2f',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)',
                      color: '#d32f2f'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" style={{color:'#d32f2f'}} />
                </IconButton>
              </Stack>
              {/* Error message displayed below without distorting layout */}
              {errVar && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Please enter unique options.
                </Typography>
              )}
              {/* Helper text for better UX */}
              {!errVar && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.70rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%',
                    color: '#666',
                    fontStyle: 'italic'
                  }}
                >
                  Press Enter to add • ESC to cancel
                </Typography>
              )}
            </Stack>
          )}
          {/* Add new item input for radio */}
          {inputType === "radio" && isAddingNewRadio && (
            <Stack 
              direction="column"
              spacing={0}
              sx={{ width: '100%', position: 'relative', mb: errVar ? 2 : 0 }}
            >
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="center" 
                spacing={0}
                sx={{ 
                  width: "100%", 
                  minHeight: 48
                }}
              >
                <TextField
                  inputRef={questionInputRef}
                  value={newRadioValue}
                  onChange={(e) => onChangeRadioval(e.target.value)}
                  placeholder={placeholder}
                  size="small"
                  variant="outlined"
                  autoFocus
                  error={errVar}
                  multiline
                  minRows={2}
                  maxRows={6}
                  sx={{ 
                    flex: 1,
                    maxWidth: 320,
                    mr: 1,
                    ml: 3,
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      color: '#000',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflow: 'auto'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#999',
                      opacity: 1
                    },
                    '& .MuiFormHelperText-root': {
                      display: 'none'
                    },
                    '& .MuiOutlinedInput-root.Mui-focused': {
                      '& fieldset': {
                        borderColor: '#FF5E4F'
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !errVar && newRadioValue.trim()) {
                      radioAddItem();
                    } else if (e.key === "Escape") {
                      cancelAddingNewRadio();
                    }
                  }}
                  onBlur={() => {
                    // Auto-save on blur if field has valid data
                    if (!errVar && newRadioValue.trim()) {
                      setTimeout(() => {
                        if (!saveButtonClickedRef.current) {
                          radioAddItem();
                        }
                        saveButtonClickedRef.current = false;
                      }, 150);
                    }
                  }}
                />
                <IconButton
                  onMouseDown={() => { saveButtonClickedRef.current = true; }}
                  onClick={radioAddItem}
                  color="primary"
                  disabled={errVar || !newRadioValue.trim()}
                  size="small"
                  aria-label="Add"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: (!errVar && newRadioValue.trim()) ? 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)' : 'transparent',
                    '&:hover': {
                      background: (!errVar && newRadioValue.trim()) ? 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)' : 'transparent'
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <AddIcon style={{color: (!errVar && newRadioValue.trim()) ? '#fff' : '#FF5E4F'}} />
                </IconButton>
                <IconButton
                  onClick={cancelAddingNewRadio}
                  size="small"
                  aria-label="Cancel"
                  sx={{ 
                    mr: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: '#d32f2f',
                    '&:hover': {
                      bgcolor: 'rgba(211, 47, 47, 0.08)',
                      color: '#d32f2f'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" style={{color:'#d32f2f'}} />
                </IconButton>
              </Stack>
              {/* Error message displayed below without distorting layout */}
              {errVar && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  Please enter unique options.
                </Typography>
              )}
              {/* Helper text for better UX */}
              {!errVar && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.70rem',
                    mt: 0.5,
                    ml: 0,
                    textAlign: 'center',
                    width: '100%',
                    color: '#666',
                    fontStyle: 'italic'
                  }}
                >
                  Press Enter to add • ESC to cancel
                </Typography>
              )}
            </Stack>
          )}
          {/* Add New Option Button for Checkbox */}
          {inputType === "checkbox" && !isAddingNewBox && (
            <Button
              onClick={startAddingNewBox}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                border: 'none',
                py: 1,
                width: '50%',
                alignSelf: 'center',
                background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)'
                }
              }}
            >
              Add New Option
            </Button>
          )}
          {/* Add New Option Button for Radio */}
          {inputType === "radio" && !isAddingNewRadio && (
            <Button
              onClick={startAddingNewRadio}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                border: 'none',
                py: 1,
                width: '50%',
                alignSelf: 'center',
                background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)'
                }
              }}
            >
              Add New Option
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ width: "70%" }}>
        <Button
          onMouseDown={() => { saveButtonClickedRef.current = true; }}
          onClick={onSaveAndClose}
          variant="contained"
          disabled={!isSaveEnabled()}
          fullWidth
          sx={{
            background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)'
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
              color: '#9e9e9e'
            }
          }}
        >
          Save
        </Button>
      </DialogActions>

      {/* Full Text View Modal */}
      <Dialog
        open={showFullTextModal}
        onClose={() => setShowFullTextModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>Full Text View</span>
          <IconButton
            onClick={() => setShowFullTextModal(false)}
            sx={{ 
              color: '#fff',
              padding: '4px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            mt: 2,
            mb: 1.5,
            px: 2.5
          }}
        >
          {!isEditingFullText ? (
            <Typography 
              sx={{ 
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: '#1f2937',
                wordBreak: 'break-word',
                bgcolor: '#f3f4f6',
                p: 2,
                borderRadius: 1.5,
                border: '1px solid #d1d5db'
              }}
            >
              {fullTextContent}
            </Typography>
          ) : (
            <TextField
              multiline
              minRows={4}
              fullWidth
              value={editFullTextValue}
              onChange={(e) => setEditFullTextValue(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused': {
                  '& fieldset': {
                    borderColor: '#FF5E4F'
                  }
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: 2.5,
            pb: 2,
            pt: 0
          }}
        >
          {!isEditingFullText ? (
            <>
              <Button
                onClick={() => {
                  // enter edit mode
                  setIsEditingFullText(true);
                  setEditFullTextValue(fullTextContent);
                }}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  py: 0.75,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)',
                    boxShadow: 'none',
                  }
                }}
              >
                Edit
              </Button>
              <Button
                onClick={() => setShowFullTextModal(false)}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  py: 0.75,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)',
                    boxShadow: 'none',
                  }
                }}
              >
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  // cancel edit
                  setIsEditingFullText(false);
                  setEditFullTextValue(fullTextContent);
                }}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  py: 0.75,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)',
                    boxShadow: 'none',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // save edits back into the appropriate array and notify parent
                  if (fullTextIndex === null) return;
                  const newVal = editFullTextValue.trim();
                  let changed = false;
                  if (inputType === 'dropdown') {
                    if (dropItems[fullTextIndex].response !== newVal) changed = true;
                    const updated = dropItems.map((it, i) => i === fullTextIndex ? { response: newVal } : it);
                    // include scores when calling onSave
                    onSave(updated.map((it, idx) => ({ response: it.response + '|' + (itemScores[idx] || 0) })))
                    setDropItems(updated);
                  } else if (inputType === 'checkbox') {
                    if (boxItems[fullTextIndex].response !== newVal) changed = true;
                    const updated = boxItems.map((it, i) => i === fullTextIndex ? { response: newVal } : it);
                    onSave(updated);
                    setBoxItems(updated);
                  } else if (inputType === 'radio') {
                    if (radioItems[fullTextIndex].response !== newVal) changed = true;
                    const updated = radioItems.map((it, i) => i === fullTextIndex ? { response: newVal } : it);
                    onSave(updated);
                    setRadioItems(updated);
                  }
                  setFullTextContent(newVal);
                  setIsEditingFullText(false);
                  if (changed) {
                    toast.success('Your changes will be saved successfully');
                  } else {
                    toast.info('No changes detected');
                  }
                }}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  py: 0.75,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #073fb1 0%, #0630a0 100%)',
                    boxShadow: 'none',
                  }
                }}
              >
                Save
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DropDownPopup;

